import { IPublicClientApplication } from '@azure/msal-browser';
import {
  NominationRole,
  Program,
  ProgramCountryLead,
  ProgramCountryNominationReviewer,
  ProgramStatus,
  RegionLead,
} from '../../models';
import { ApiEndpointCallStatesStore } from '../api-endpoint-call-states-store';
import { FetchContext } from '../fetch-context';
import { promiseWithObservableState } from '../promise-with-observable-state';
import { updateProgramCountryNominationRole } from '../staff-programs';
import { withApiAccessToken } from '../with-api-access-token';
import {
  assignProgramCountryLead,
  assignProgramCountryNominationReviewer,
  assignRegionLead,
  createProgram,
  deleteProgramCountryLead,
  deleteProgramCountryNominationReviewer,
  deleteRegionLead,
  getCountryNominationRefs,
  getDefaultProgramRef,
  getProgramCountryAward,
  getProgramCountryLeads,
  getProgramCountryNominationReviewers,
  getProgramRegionLeads,
  getProgramRegions,
  getPrograms,
  getScrubCountries,
  getStaffProgramWinnerRegions,
  updateProgramStatus,
} from './index';
import { getAvailableProgramCountryNominationReviewers } from './get-available-program-country-nomination-reviewers';

export const createProgramsApiEndpoints = (
  fetchContext: FetchContext,
  apiEndpointCallStates: ApiEndpointCallStatesStore,
) => ({
  getDefaultProgramRef: (instance: IPublicClientApplication) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getDefaultProgramRef(fetchContext, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getDefaultProgramRef', isInFlight);
    },
  ),
  getProgramCountryAward: (instance: IPublicClientApplication, programId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getProgramCountryAward(fetchContext, programId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getProgramCountryAward', isInFlight);
    },
  ),
  getProgramCountryLeads: (instance: IPublicClientApplication, programId: number, countryId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getProgramCountryLeads(fetchContext, programId, countryId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getProgramCountryLeads', isInFlight);
    },
  ),
  getProgramCountryNominationReviewers: (instance: IPublicClientApplication, programId: number, countryId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getProgramCountryNominationReviewers(fetchContext, programId, countryId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getProgramCountryNominationReviewers', isInFlight);
    },
  ),
  getAvailableProgramCountryNominationReviewers: (instance: IPublicClientApplication, programId: number, countryId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getAvailableProgramCountryNominationReviewers(fetchContext, programId, countryId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getAvailableProgramCountryNominationReviewers', isInFlight);
    },
  ),
  getProgramRegions: (instance: IPublicClientApplication, programId: number, searchText?: string) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getProgramRegions(fetchContext, programId, searchText, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getProgramRegions', isInFlight);
    },
  ),
  getProgramRegionLeads: (instance: IPublicClientApplication, programId: number, regionId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getProgramRegionLeads(fetchContext, programId, regionId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getProgramRegionLeads', isInFlight);
    },
  ),
  getScrubCountries: (instance: IPublicClientApplication, programId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getScrubCountries(fetchContext, programId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getScrubCountries', isInFlight);
    },
  ),
  updateProgramCountryNominationRole: (instance: IPublicClientApplication, programId: number, countryId: number, nominationId: number, nominationRole: NominationRole) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => updateProgramCountryNominationRole(fetchContext, programId, countryId, nominationId, nominationRole, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('updateProgramCountryNominationRole', isInFlight);
    },
  ),
  getPrograms: (instance: IPublicClientApplication) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getPrograms(fetchContext, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getPrograms', isInFlight);
    },
  ),
  createProgram: (instance: IPublicClientApplication, program: Program) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => createProgram(fetchContext, program, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('createProgram', isInFlight);
    },
  ),
  updateProgramStatus: (instance: IPublicClientApplication, programId: number, programStatus: ProgramStatus) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => updateProgramStatus(fetchContext, programId, programStatus, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('updateProgramStatus', isInFlight);
    },
  ),
  getStaffProgramWinnerRegions: (instance: IPublicClientApplication, programId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getStaffProgramWinnerRegions(fetchContext, programId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getStaffProgramWinnerRegions', isInFlight);
    },
  ),
  getCountryNominationRefs: (instance: IPublicClientApplication, programId: number, countryId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getCountryNominationRefs(fetchContext, programId, countryId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getCountryNominationRefs', isInFlight);
    },
  ),
  assignProgramCountryLead: (instance: IPublicClientApplication, programId: number, countryId: number, programCountryLead: ProgramCountryLead) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => assignProgramCountryLead(fetchContext, programId, countryId, programCountryLead, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('assignProgramCountryLead', isInFlight);
    },
  ),
  deleteProgramCountryLead: (instance: IPublicClientApplication, programCountryLeadId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => deleteProgramCountryLead(fetchContext, programCountryLeadId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('deleteProgramCountryLead', isInFlight);
    },
  ),
  assignProgramCountryNominationReviewer: (instance: IPublicClientApplication, programId: number, countryId: number, programCountryNominationReviewer: ProgramCountryNominationReviewer) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => assignProgramCountryNominationReviewer(fetchContext, programId, countryId, programCountryNominationReviewer, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('assignProgramCountryNominationReviewer', isInFlight);
    },
  ),
  deleteProgramCountryNominationReviewer: (instance: IPublicClientApplication, programCountryNominationReviewerId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => deleteProgramCountryNominationReviewer(fetchContext, programCountryNominationReviewerId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('deleteProgramCountryNominationReviewer', isInFlight);
    },
  ),
  assignRegionLead: (instance: IPublicClientApplication, programId: number, regionId: number, regionLead: RegionLead) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => assignRegionLead(fetchContext, programId, regionId, regionLead, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('assignRegionLead', isInFlight);
    },
  ),
  deleteRegionLead: (instance: IPublicClientApplication, programRegionLeadId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => deleteRegionLead(fetchContext, programRegionLeadId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('deleteRegionLead', isInFlight);
    },
  ),
});
